import 'amazon-connect-streams';
import { useEffect, useRef } from 'react';
import { useEmailContext } from '../../../contexts/EmailContext';
import { extractEmailData } from '../../../utils/emailContactHelpers';
import { EmailAttachment, EmailMessage } from '../../../models/Email';
import { fetchEmailThread, ThreadAttachment, ThreadContact } from '../../../services/fetchEmailThread';
import { EMAIL_INITIATION_METHODS } from '../../../constants/email';
import DOMPurify from 'dompurify';
import { useEmailSanitizationLogger } from './useEmailSanitizationLogger';

export function filterThreadContacts(
  contacts: ThreadContact[],
  activeContactId: string
): { filtered: ThreadContact[]; wasTransferred: boolean } {
  const previousContactIdsSet = new Set(contacts.map(c => c.previousContactId).filter(Boolean));
  const filtered = contacts.filter(c => c.contactId === activeContactId || !previousContactIdsSet.has(c.contactId));
  const activeContact = contacts.find(c => c.contactId === activeContactId);
  const wasTransferred =
    !!activeContact && activeContact.initiationMethod.toUpperCase() === EMAIL_INITIATION_METHODS.TRANSFER;
  return { filtered, wasTransferred };
}

export function resolveThreadAttachments(attachments: ThreadAttachment[] | undefined): {
  attachments: EmailAttachment[];
  hasRejectedAttachments: boolean;
} {
  const list = attachments ?? [];

  if (list.some(a => a.scanStatus === 'rejected')) {
    return { attachments: [], hasRejectedAttachments: true };
  }

  return {
    attachments: list.map(a => ({ fileId: a.fileId, fileName: a.fileName })),
    hasRejectedAttachments: false
  };
}

interface UseEmailContactHandlersProps {
  onError?: (message: string) => void;
}

export function useEmailContactHandlers({ onError }: UseEmailContactHandlersProps): void {
  const { updateEmailData, updateEmailThread, startLoadingThread, stopLoadingThread } = useEmailContext();
  const { logSanitization } = useEmailSanitizationLogger();
  const isInitializedRef = useRef(false);
  const onErrorRef = useRef(onError);

  onErrorRef.current = onError;

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }
    isInitializedRef.current = true;

    const handleExtractEmailData = (contact: connect.Contact): void => {
      updateEmailData(contact.getContactId(), extractEmailData(contact));
    };

    const sanitizeBodyHtml = (rawBodyHtml: string | null | undefined, contactId: string): string | undefined => {
      if (!rawBodyHtml) return undefined;

      logSanitization({ contactId, outcome: 'attempted' });

      try {
        const sanitizedHtml = DOMPurify.sanitize(rawBodyHtml);
        logSanitization({ contactId, outcome: sanitizedHtml !== rawBodyHtml ? 'modified' : 'unchanged' });
        return sanitizedHtml;
      } catch (error) {
        logSanitization({ contactId, outcome: 'failed', error });
        return undefined;
      }
    };

    const mapThreadContactToEmailMessage = (threadContact: ThreadContact): EmailMessage => {
      const { subject, from, to, cc, direction, deliveredTo, attachments } = threadContact;

      const toRecipients = [
        ...(deliveredTo ? [{ emailAddress: deliveredTo }] : []),
        ...(to ?? []).map(emailAddress => ({ emailAddress }))
      ];

      const { attachments: resolvedAttachments, hasRejectedAttachments } = resolveThreadAttachments(attachments);

      return {
        contactId: threadContact.contactId,
        subject: subject ?? undefined,
        from: from ? { emailAddress: from } : undefined,
        to: toRecipients,
        cc: (cc ?? []).map(emailAddress => ({ emailAddress })),
        bodyHtml: sanitizeBodyHtml(threadContact.bodyHtml, threadContact.contactId),
        attachments: resolvedAttachments,
        timestamp: new Date(threadContact.initiationTimestamp).getTime(),
        direction: direction === 'INBOUND' || direction === 'OUTBOUND' 
          ? direction
          : undefined,
        hasRejectedAttachments
      };
    };

    const handleFetchEmailThread = async (contact: connect.Contact): Promise<void> => {
      const activeContactId = contact.getContactId();
      const contactAssociationId = contact.getContactAssociationId();
      if (!contactAssociationId) return;

      try {
        startLoadingThread(activeContactId);
        const contacts = await fetchEmailThread(contactAssociationId);

        const { filtered: filteredContacts, wasTransferred } = filterThreadContacts(contacts, activeContactId);

        const messages: EmailMessage[] = filteredContacts
          .map(mapThreadContactToEmailMessage)
          .sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));

        updateEmailThread(activeContactId, { contactAssociationId, messages, wasTransferred });
      } catch (error) {
        console.error('Failed to fetch email thread:', error);
        onErrorRef.current?.('Failed to load the email thread. Please try again.');
      } finally {
        stopLoadingThread(activeContactId);
      }
    };

    const handleEmailContact = (contact: connect.Contact): void => {
      handleExtractEmailData(contact);

      contact.onConnected(() => {
        handleExtractEmailData(contact);
        void handleFetchEmailThread(contact);
      });
    };

    connect.agent(agent => {
      agent
        .getContacts()
        .filter(c => (c.getType() as string) === connect.ContactType.EMAIL)
        .filter(c => c.getInitiationMethod()?.toUpperCase() !== EMAIL_INITIATION_METHODS.AGENT_REPLY)
        .forEach(handleEmailContact);
    });

    connect.contact(contact => {
      if ((contact.getType() as string) !== connect.ContactType.EMAIL) {
        return;
      }

      const initiationMethod = contact.getInitiationMethod() as string;

      if (initiationMethod?.toUpperCase() === EMAIL_INITIATION_METHODS.AGENT_REPLY) {
        return;
      }

      handleEmailContact(contact);
    });
  }, [logSanitization, startLoadingThread, stopLoadingThread, updateEmailData, updateEmailThread]);
}
